<template>
  <div class="fixed inset-0 bg-gray-900/50 flex justify-center items-center p-4 z-50">
    <div class="bg-white w-full mx-24 max-h-[90vh] rounded-lg shadow-xl flex flex-col">
      <!-- Header -->
      <div class="font-bold text-lg py-4 px-6 border-b flex justify-between items-center">
        <span>Create New Entity</span>
        <button @click="closeForm" class="text-gray-500 hover:text-gray-700">
          <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"></path>
          </svg>
        </button>
      </div>

      <!-- Progress Indicator -->
      <div class="px-6 py-4 border-b bg-gray-50">
        <div class="flex items-center justify-between">
          <div
            v-for="(step, index) in steps"
            :key="index"
            class="flex items-center"
            :class="{ 'flex-1': index < steps.length - 1 }"
          >
            <div class="flex items-center">
              <div
                class="w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors"
                :class="getStepClasses(index)"
              >
                <span v-if="index < currentStep"></span>
                <span v-else>{{ index + 1 }}</span>
              </div>
              <span
                class="ml-2 text-sm font-medium"
                :class="index === currentStep ? 'text-blue-600' : index < currentStep ? 'text-green-600' : 'text-gray-400'"
              >
                {{ step.name }}
              </span>
            </div>
            <div
              v-if="index < steps.length - 1"
              class="flex-1 h-0.5 mx-4"
              :class="index < currentStep ? 'bg-green-600' : 'bg-gray-300'"
            ></div>
          </div>
        </div>
      </div>

      <!-- Step Content -->
      <div class="flex-1 overflow-y-auto px-6 py-6">
        <!-- Step 1: Basic Info -->
        <div v-if="currentStep === 0">
          <h3 class="text-md font-semibold mb-4">Basic Information</h3>

          <div class="mb-4">
            <label for="entityName" class="block text-sm font-medium text-gray-700 mb-2">
              Entity Name <span class="text-red-500">*</span>
            </label>
            <input
              id="entityName"
              v-model="formData.name"
              type="text"
              class="w-full p-2 text-sm border rounded-md focus:ring-2 focus:ring-blue-dark focus:border-blue-500"
              :class="{ 'border-red-300 bg-red-50': errors.name }"
              placeholder="Enter entity name"
              @input="clearError('name')"
            />
            <p v-if="errors.name" class="text-red-500 text-xs mt-1">{{ errors.name }}</p>

            <div v-if="existingMatches.length > 0" class="mt-3 p-3 bg-yellow-50 border border-yellow-300 rounded-md">
              <p class="text-sm font-medium text-yellow-900 mb-2">
                {{ existingMatches.length }} {{ getEntityTypeName(formData.entity_type) }} entit{{ existingMatches.length === 1 ? 'y' : 'ies' }} with similar names already exist:
              </p>
              <ul class="text-sm space-y-1 mb-3">
                <li v-for="match in existingMatches" :key="match.id">
                  <router-link :to="`/entity/${match.id}`" target="_blank" class="text-blue-600 hover:underline">
                    {{ match.name }}
                  </router-link>
                  <span class="text-gray-500 text-xs ml-1">(id {{ match.id }})</span>
                </li>
              </ul>
              <label class="flex items-center text-sm text-yellow-900">
                <input type="checkbox" v-model="acknowledgedMatches" class="mr-2" />
                Create anyway — I've reviewed these and need a new entity.
              </label>
            </div>
          </div>

          <div v-if="!entityTypeName" class="mb-4">
            <label for="entityType" class="block text-sm font-medium text-gray-700 mb-2">
              Entity Type <span class="text-red-500">*</span>
            </label>
            <select
              id="entityType"
              v-model="formData.entity_type"
              class="w-full p-2 text-sm border rounded-md focus:ring-2 focus:ring-blue-dark focus:border-blue-500"
              :class="{ 'border-red-300 bg-red-50': errors.entity_type }"
              @change="onEntityTypeChange"
            >
              <option value="">Select entity type...</option>
              <option v-for="type in entityTypes" :key="type.id" :value="type.id">
                {{ type.type_name }}
              </option>
            </select>
            <p v-if="errors.entity_type" class="text-red-500 text-xs mt-1">{{ errors.entity_type }}</p>
          </div>

          <!-- Display locked entity type if provided via prop -->
          <div v-else class="mb-4">
            <label class="block text-sm font-medium text-gray-700 mb-2">
              Entity Type
            </label>
            <div class="w-full p-2 text-sm border border-gray-300 rounded-md bg-gray-100 text-gray-700">
              {{ entityTypeName }}
              <span class="text-xs text-gray-500 ml-2">(pre-selected)</span>
            </div>
          </div>
        </div>

        <!-- Step 2: Identifiers -->
        <div v-if="currentStep === 1">
          <h3 class="text-md font-semibold mb-4">Entity Identifiers</h3>
          <p class="text-sm text-gray-600 mb-4">
            Add identifiers such as NPI, TIN, or MRN to help uniquely identify this entity.
          </p>

          <!-- Identifier List -->
          <div v-if="formData.identifiers.length > 0" class="mb-4">
            <div
              v-for="(identifier, index) in formData.identifiers"
              :key="index"
              class="flex items-center gap-2 mb-2 p-2 bg-gray-50 rounded border"
            >
              <span class="text-xs font-medium text-gray-600 w-24">
                {{ getIdentifierTypeName(identifier.identifier_type) }}:
              </span>
              <span class="text-sm flex-1">{{ identifier.identifier }}</span>
              <button
                @click="removeIdentifier(index)"
                class="text-red-600 hover:text-red-800"
                type="button"
              >
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          <!-- Add Identifier Form -->
          <div class="bg-blue-50 border border-blue-200 rounded-md p-4">
            <h4 class="text-sm font-medium mb-4">Add Identifier</h4>
            <div class="flex gap-2">
              <select
                v-model="newIdentifier.identifier_type"
                class="p-2 text-sm border rounded-md flex-1"
              >
                <option value="">Select type...</option>
                <option
                  v-for="type in availableIdentifierTypes"
                  :key="type.id"
                  :value="type.id"
                >
                  {{ type.type_name }}
                </option>
              </select>
              <input
                v-model="newIdentifier.identifier"
                type="text"
                placeholder="Enter identifier value"
                class="p-2 text-sm border rounded-md flex-1"
                :disabled="!newIdentifier.identifier_type"
                @keyup.enter="addIdentifier"
              />
              <button
                @click="addIdentifier"
                :disabled="!newIdentifier.identifier_type || !newIdentifier.identifier"
                class="btn-primary-sm"
                type="button"
              >
                Add
              </button>
            </div>
          </div>
        </div>

        <!-- Step 3: Parent Relationships -->
        <div v-if="currentStep === 2">
          <h3 class="text-md font-semibold mb-4">Parent Relationships</h3>
          <p class="text-sm text-gray-600 mb-4">
            Define hierarchical relationships by assigning parent entities (e.g., a Provider belongs to a Practice).
          </p>

          <!-- Current Parents -->
          <div v-if="formData.parents.length > 0" class="mb-4">
            <h4 class="text-sm font-medium mb-2">Selected Parents</h4>
            <div
              v-for="(parent, index) in formData.parents"
              :key="index"
              class="flex items-center gap-2 mb-2 p-3 bg-gray-50 rounded border"
            >
              <div class="flex-1">
                <div class="text-sm font-medium">{{ parent.name }}</div>
                <div class="text-xs text-gray-500">{{ parent.entity_type_name }}</div>
              </div>
              <button
                @click="removeParent(index)"
                class="text-red-600 hover:text-red-800"
                type="button"
              >
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          <!-- Add Parent Form -->
          <div class="bg-blue-50 border border-blue-200 rounded-md p-4">
            <h4 class="text-sm font-medium mb-4">Add Parent Entity</h4>

            <EntitySearchSingle
              button-text="Select Parent Entity"
              modal-title="Select Parent Entity"
              button-class="w-full"
              @entity-selected="onParentEntitySelected"
            />
          </div>
        </div>

        <!-- Step 4: Manual Attributes -->
        <div v-if="currentStep === 3">
          <h3 class="text-md font-semibold mb-4">Entity Attributes</h3>
          <p class="text-sm text-gray-600 mb-4">
            Fill in additional details for this entity. Required fields are marked with <span class="text-red-500">*</span>
          </p>

          <div v-if="manualAttributes.length === 0" class="text-sm text-gray-500 italic">
            No manual attributes configured for this entity type.
          </div>

          <!-- Attribute groups -->
          <div v-for="group in attributeGroups" :key="group" class="mb-6">
            <h4 class="text-sm font-semibold text-gray-700 mb-3 pb-1 border-b">
              {{ group || 'General' }}
            </h4>

            <div
              v-for="attr in getAttributesByGroup(group)"
              :key="attr.id"
              class="mb-4"
            >
              <label :for="`attr-${attr.id}`" class="block text-sm font-medium text-gray-700 mb-2">
                {{ attr.display_name }}
                <span v-if="attr.is_required" class="text-red-500">*</span>
              </label>

              <!-- Text input -->
              <input
                v-if="attr.field_type === 'text' || attr.field_type === 'email' || attr.field_type === 'phone' || attr.field_type === 'url'"
                :id="`attr-${attr.id}`"
                v-model="formData.attributes[attr.name]"
                :type="attr.field_type === 'email' ? 'email' : attr.field_type === 'url' ? 'url' : 'text'"
                :placeholder="attr.description"
                class="w-full p-2 text-sm border rounded-md focus:ring-2 focus:ring-blue-dark focus:border-blue-500"
                :class="{ 'border-red-300 bg-red-50': errors[`attr_${attr.name}`] }"
              />

              <!-- Number input -->
              <input
                v-else-if="attr.field_type === 'number'"
                :id="`attr-${attr.id}`"
                v-model.number="formData.attributes[attr.name]"
                type="number"
                step="any"
                :placeholder="attr.description"
                class="w-full p-2 text-sm border rounded-md focus:ring-2 focus:ring-blue-dark focus:border-blue-500"
                :class="{ 'border-red-300 bg-red-50': errors[`attr_${attr.name}`] }"
              />

              <!-- Date input -->
              <input
                v-else-if="attr.field_type === 'date'"
                :id="`attr-${attr.id}`"
                v-model="formData.attributes[attr.name]"
                type="date"
                class="w-full p-2 text-sm border rounded-md focus:ring-2 focus:ring-blue-dark focus:border-blue-500"
                :class="{ 'border-red-300 bg-red-50': errors[`attr_${attr.name}`] }"
              />

              <!-- Boolean checkbox -->
              <div v-else-if="attr.field_type === 'boolean'" class="flex items-center">
                <input
                  :id="`attr-${attr.id}`"
                  v-model="formData.attributes[attr.name]"
                  type="checkbox"
                  class="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-dark"
                />
                <label :for="`attr-${attr.id}`" class="ml-2 text-sm text-gray-600">
                  {{ attr.description || 'Yes' }}
                </label>
              </div>

              <!-- JSON textarea -->
              <textarea
                v-else-if="attr.field_type === 'json'"
                :id="`attr-${attr.id}`"
                v-model="formData.attributes[attr.name]"
                rows="3"
                :placeholder="attr.description || 'Enter JSON data'"
                class="w-full p-2 text-sm border rounded-md focus:ring-2 focus:ring-blue-dark focus:border-blue-500 font-mono"
                :class="{ 'border-red-300 bg-red-50': errors[`attr_${attr.name}`] }"
              />

              <p v-if="errors[`attr_${attr.name}`]" class="text-red-500 text-xs mt-1">
                {{ errors[`attr_${attr.name}`] }}
              </p>
              <p v-if="attr.description && attr.field_type !== 'boolean'" class="text-gray-500 text-xs mt-1">
                {{ attr.description }}
              </p>
            </div>
          </div>

          <div class="mt-6 p-4 bg-blue-50 rounded border text-sm text-gray-600">
            <p><strong>Tip:</strong> After creating the entity, you can use AI-powered enrichment to automatically populate additional attributes from web sources.</p>
          </div>
        </div>
      </div>

      <!-- Footer with Navigation -->
      <div class="border-t px-6 py-4 flex justify-between bg-gray-50">
        <button
          v-if="currentStep > 0"
          @click="previousStep"
          class="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 text-sm rounded-md transition-colors"
          type="button"
        >
          Previous
        </button>
        <div v-else></div>

        <div class="flex gap-2">
          <button
            @click="closeForm"
            class="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 text-sm rounded-md transition-colors"
            type="button"
          >
            Cancel
          </button>

          <button
            v-if="currentStep < steps.length - 1"
            @click="nextStep"
            :disabled="!canProceed"
            class="btn-primary-sm"
            type="button"
          >
            Next
          </button>

          <button
            v-if="currentStep === steps.length - 1"
            @click="finishAndClose"
            :disabled="submitting"
            class="px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            type="button"
          >
            <svg v-if="submitting" class="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <span>{{ submitting ? 'Creating...' : 'Create Entity' }}</span>
          </button>
        </div>
      </div>

      <!-- Global Error Message -->
      <div v-if="globalError" class="mx-6 mb-4 p-4 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
        {{ globalError }}
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import EntitySearchSingle from '@/components/widgetcomponents/Search_Widgets/EntitySearch_Single.vue';

export default {
  name: 'FormEntity',

  components: {
    EntitySearchSingle
  },

  props: {
    isVisible: {
      type: Boolean,
      default: false
    },
    entityTypeName: {
      type: String,
      default: null
    },
    parentEntityId: {
      type: [Number, String],
      default: null
    }
  },

  data() {
    return {
      currentStep: 0,
      steps: [
        { name: 'Basic Info', required: true },
        { name: 'Identifiers', required: false },
        { name: 'Parent Relationships', required: false },
        { name: 'Attributes', required: false }
      ],

      formData: {
        name: '',
        entity_type: '',
        identifiers: [],
        parents: [],
        attributes: {}
      },

      entityTypes: [],
      identifierTypes: [],
      entityAttributes: [],

      newIdentifier: {
        identifier_type: '',
        identifier: ''
      },

      errors: {},
      globalError: '',

      createdEntityId: null,
      submitting: false,

      existingMatches: [],
      matchCheckTimer: null,
      acknowledgedMatches: false
    };
  },

  watch: {
    'formData.name'() { this.scheduleMatchCheck(); },
    'formData.entity_type'() { this.scheduleMatchCheck(); }
  },

  computed: {
    canProceed() {
      if (this.currentStep === 0) {
        return this.formData.name
          && this.formData.entity_type
          && (this.existingMatches.length === 0 || this.acknowledgedMatches);
      }
      return true;
    },

    availableIdentifierTypes() {
      const usedTypes = this.formData.identifiers.map(i => i.identifier_type);
      return this.identifierTypes.filter(t => !usedTypes.includes(t.id));
    },

    manualAttributes() {
      return this.entityAttributes.filter(attr => attr.manual_only || !attr.manual_only);
    },

    attributeGroups() {
      const groups = [...new Set(this.manualAttributes.map(attr => attr.display_group || ''))];
      return groups.sort();
    }
  },

  async mounted() {
    await this.fetchEntityTypes();
    await this.fetchIdentifierTypes();

    // If entity type is provided as prop, set it by name and fetch attributes
    if (this.entityTypeName) {
      const entityType = this.entityTypes.find(t => t.type_name === this.entityTypeName);
      if (entityType) {
        this.formData.entity_type = entityType.id;
        await this.fetchEntityAttributes();
      }
    }

    // If parent entity is provided as prop, add it to parents list
    if (this.parentEntityId) {
      // Fetch the parent entity details to get its type
      try {
        const response = await axios.get(`/api/entities/${this.parentEntityId}/`);
        const parentEntity = response.data;
        const entityType = this.entityTypes.find(t => t.id === parentEntity.entity_type);

        this.formData.parents.push({
          id: parentEntity.id,
          name: parentEntity.name,
          entity_type: parentEntity.entity_type,
          entity_type_name: entityType ? entityType.type_name : 'Unknown'
        });
      } catch (error) {
        console.error('Error fetching parent entity:', error);
      }
    }
  },

  methods: {
    getStepClasses(index) {
      if (index === this.currentStep) {
        return 'bg-primary-500 text-white';
      } else if (index < this.currentStep) {
        return 'bg-green-600 text-white';
      } else {
        return 'bg-gray-300 text-gray-600';
      }
    },

    scheduleMatchCheck() {
      this.acknowledgedMatches = false;
      if (this.matchCheckTimer) clearTimeout(this.matchCheckTimer);
      this.matchCheckTimer = setTimeout(this.checkExistingMatches, 300);
    },

    async checkExistingMatches() {
      if (!this.formData.name || !this.formData.entity_type) {
        this.existingMatches = [];
        return;
      }
      const response = await axios.get('/api/entities/', {
        params: { name: this.formData.name, entity_type_id: this.formData.entity_type }
      });
      const data = Array.isArray(response.data) ? response.data : (response.data.results || []);
      this.existingMatches = data.slice(0, 5);
    },

    async fetchEntityTypes() {
      try {
        const response = await axios.get('/api/entity-types/');
        this.entityTypes = response.data;
      } catch (error) {
        console.error('Error fetching entity types:', error);
        this.globalError = 'Failed to load entity types';
      }
    },

    async fetchIdentifierTypes() {
      try {
        const response = await axios.get('/api/identifiertypes/');
        this.identifierTypes = response.data;
      } catch (error) {
        console.error('Error fetching identifier types:', error);
      }
    },

    async onEntityTypeChange() {
      this.clearError('entity_type');
      if (this.formData.entity_type) {
        await this.fetchEntityAttributes();
      }
    },

    async fetchEntityAttributes() {
      try {
        const response = await axios.get('/api/entity-attributes/', {
          params: { entity_type_id: this.formData.entity_type }
        });
        this.entityAttributes = response.data;

        // Initialize attribute values
        this.formData.attributes = {};
        this.entityAttributes.forEach(attr => {
          if (attr.field_type === 'boolean') {
            this.formData.attributes[attr.name] = false;
          } else {
            this.formData.attributes[attr.name] = '';
          }
        });
      } catch (error) {
        console.error('Error fetching entity attributes:', error);
      }
    },

    getEntityTypeName(typeId) {
      const type = this.entityTypes.find(t => t.id === parseInt(typeId));
      return type ? type.type_name : 'Unknown';
    },

    getIdentifierTypeName(typeId) {
      const type = this.identifierTypes.find(t => t.id === typeId);
      return type ? type.type_name : 'Unknown';
    },

    addIdentifier() {
      if (this.newIdentifier.identifier_type && this.newIdentifier.identifier) {
        this.formData.identifiers.push({
          identifier_type: this.newIdentifier.identifier_type,
          identifier: this.newIdentifier.identifier
        });
        this.newIdentifier = { identifier_type: '', identifier: '' };
      }
    },

    removeIdentifier(index) {
      this.formData.identifiers.splice(index, 1);
    },

    onParentEntitySelected(entity) {
      // Check if already added
      if (this.formData.parents.some(p => p.id === entity.id)) {
        this.globalError = 'This parent entity has already been added';
        setTimeout(() => { this.globalError = ''; }, 3000);
        return;
      }

      this.formData.parents.push({
        id: entity.id,
        name: entity.name,
        entity_type: entity.entity_type,
        entity_type_name: entity.entity_type_name
      });
    },

    removeParent(index) {
      this.formData.parents.splice(index, 1);
    },

    getAttributesByGroup(group) {
      return this.manualAttributes.filter(attr => (attr.display_group || '') === group);
    },

    validateStep() {
      this.errors = {};
      this.globalError = '';

      if (this.currentStep === 0) {
        if (!this.formData.name) {
          this.errors.name = 'Entity name is required';
        }
        if (!this.formData.entity_type) {
          this.errors.entity_type = 'Entity type is required';
        }
        return Object.keys(this.errors).length === 0;
      }

      if (this.currentStep === 3) {
        // Validate required attributes
        let isValid = true;
        this.manualAttributes.forEach(attr => {
          if (attr.is_required) {
            const value = this.formData.attributes[attr.name];
            if (value === '' || value === null || value === undefined) {
              this.errors[`attr_${attr.name}`] = `${attr.display_name} is required`;
              isValid = false;
            }
          }
        });
        return isValid;
      }

      return true;
    },

    async nextStep() {
      if (!this.validateStep()) {
        return;
      }

      // Just move to next step - don't save anything yet
      this.currentStep++;
    },

    previousStep() {
      if (this.currentStep > 0) {
        this.currentStep--;
      }
    },

    async createEntity() {
      try {
        const response = await axios.post('/api/entities/', {
          name: this.formData.name,
          entity_type: this.formData.entity_type
        });
        this.createdEntityId = response.data.id;
        console.log('Created entity:', this.createdEntityId);
      } catch (error) {
        console.error('Error creating entity:', error);
        this.globalError = error.response?.data?.detail || 'Failed to create entity';
        throw error;
      }
    },

    async saveIdentifiers() {
      try {
        for (const identifier of this.formData.identifiers) {
          await axios.post('/api/identifiers/', {
            entity: this.createdEntityId,
            identifier_type: identifier.identifier_type,
            identifier: identifier.identifier
          });
        }
      } catch (error) {
        console.error('Error saving identifiers:', error);

        // Extract error message from response
        let errorMessage = 'Some identifiers could not be saved';

        if (error.response?.data) {
          // Check for identifier field error
          if (error.response.data.identifier) {
            errorMessage = Array.isArray(error.response.data.identifier)
              ? error.response.data.identifier[0]
              : error.response.data.identifier;
          }
          // Check for detail field
          else if (error.response.data.detail) {
            errorMessage = error.response.data.detail;
          }
          // Check for non_field_errors
          else if (error.response.data.non_field_errors) {
            errorMessage = Array.isArray(error.response.data.non_field_errors)
              ? error.response.data.non_field_errors[0]
              : error.response.data.non_field_errors;
          }
        }

        this.globalError = errorMessage;

        // Don't throw - let user continue and fix the issue
        // They can go back to step 1 to adjust identifiers
      }
    },

    async saveParents() {
      try {
        for (const parent of this.formData.parents) {
          await axios.post(`/api/entities/${this.createdEntityId}/add-parent/`, {
            parent_id: parent.id
          });
        }
      } catch (error) {
        console.error('Error saving parents:', error);
        this.globalError = error.response?.data?.error || 'Failed to add parent relationships';
      }
    },

    async saveAttributes() {
      try {
        for (const [attrName, value] of Object.entries(this.formData.attributes)) {
          // Skip empty values
          if (value === '' || value === null || value === undefined) {
            continue;
          }

          await axios.post(`/api/entities/${this.createdEntityId}/update_field/`, {
            field_name: attrName,
            field_value: value,
            data_source_name: 'Manual Entry',
            reason: 'Initial creation'
          });
        }
      } catch (error) {
        console.error('Error saving attributes:', error);
        this.globalError = 'Some attributes could not be saved';
      }
    },

    async finishAndClose() {
      // Validate final step
      if (!this.validateStep()) {
        return;
      }

      this.submitting = true;
      this.globalError = '';

      try {
        // 1. Create the entity
        await this.createEntity();
        if (!this.createdEntityId) {
          this.globalError = 'Failed to create entity';
          return;
        }

        // 2. Save all the data in sequence
        if (this.formData.identifiers.length > 0) {
          await this.saveIdentifiers();
        }

        if (this.formData.parents.length > 0) {
          await this.saveParents();
        }

        if (Object.keys(this.formData.attributes).some(key => {
          const val = this.formData.attributes[key];
          return val !== '' && val !== null && val !== undefined;
        })) {
          await this.saveAttributes();
        }

        // 3. Navigate to entity detail page
        this.$emit('close');
        this.$emit('update');

        if (this.createdEntityId) {
          this.$router.push(`/entity/${this.createdEntityId}`);
        }
      } catch (error) {
        console.error('Error creating entity:', error);
        this.globalError = error.response?.data?.detail || 'Failed to create entity';
      } finally {
        this.submitting = false;
      }
    },

    closeForm() {
      // Reset form
      this.currentStep = 0;
      this.formData = {
        name: '',
        entity_type: '',
        identifiers: [],
        parents: [],
        attributes: {}
      };
      this.createdEntityId = null;
      this.submitting = false;
      this.errors = {};
      this.globalError = '';

      this.$emit('close');
    },

    clearError(field) {
      if (this.errors[field]) {
        delete this.errors[field];
      }
    }
  }
};
</script>

<style scoped>
input:focus,
select:focus,
textarea:focus {
  outline: none;
}
</style>
